import { Fragment } from 'react';

/**
 * LegalText — muestra el texto de un documento legal.
 *
 * El texto se guarda con un formato sencillo (el mismo que se edita en el
 * panel de admin): `#` y `##` para títulos, `-` o `1.` para listas, `>` para
 * notas y `**negritas**`. Se dibuja con elementos de React, nunca como HTML
 * crudo, así que el contenido no puede inyectar código en la página.
 */

const inline = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4
      ? <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>
      : <Fragment key={i}>{part}</Fragment>,
  );

const parseBlocks = (content) => {
  const blocks = [];
  let paragraph = [];
  let list = null;

  const flush = () => {
    if (paragraph.length) {
      blocks.push({ type: 'p', text: paragraph.join(' ') });
      paragraph = [];
    }
    if (list) {
      blocks.push(list);
      list = null;
    }
  };

  for (const raw of (content || '').split('\n')) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    const bullet = line.match(/^[-*]\s+(.*)$/);
    const numbered = line.match(/^\d+[.)]\s+(.*)$/);
    const note = line.match(/^>\s?(.*)$/);
    if (heading) {
      flush();
      blocks.push({ type: `h${heading[1].length}`, text: heading[2] });
    } else if (bullet || numbered) {
      const type = bullet ? 'ul' : 'ol';
      if (paragraph.length || (list && list.type !== type)) flush();
      if (!list) list = { type, items: [] };
      list.items.push((bullet || numbered)[1]);
    } else if (note) {
      flush();
      blocks.push({ type: 'note', text: note[1] });
    } else if (list) {
      // Renglón que continúa el último elemento de la lista
      list.items[list.items.length - 1] += ` ${line}`;
    } else {
      paragraph.push(line);
    }
  }
  flush();
  return blocks;
};

const LegalText = ({ content }) => (
  <div className="space-y-4 font-manrope" data-testid="legal-text">
    {parseBlocks(content).map((block, i) => {
      switch (block.type) {
        case 'h1':
          return <h1 key={i} className="font-teko text-4xl text-white uppercase">{inline(block.text)}</h1>;
        case 'h2':
          return <h2 key={i} className="font-teko text-2xl text-white uppercase pt-4">{inline(block.text)}</h2>;
        case 'h3':
          return <h3 key={i} className="text-white font-semibold pt-2">{inline(block.text)}</h3>;
        case 'ul':
          return (
            <ul key={i} className="list-disc pl-5 space-y-1 text-zinc-300 text-sm leading-relaxed">
              {block.items.map((item, j) => <li key={j}>{inline(item)}</li>)}
            </ul>
          );
        case 'ol':
          return (
            <ol key={i} className="list-decimal pl-5 space-y-1 text-zinc-300 text-sm leading-relaxed">
              {block.items.map((item, j) => <li key={j}>{inline(item)}</li>)}
            </ol>
          );
        case 'note':
          return (
            <p key={i} className="text-xs text-primary border border-primary/30 bg-primary/5 rounded-sm p-3">
              {inline(block.text)}
            </p>
          );
        default:
          return <p key={i} className="text-zinc-300 text-sm leading-relaxed">{inline(block.text)}</p>;
      }
    })}
  </div>
);

export default LegalText;
